import { Channel, Topic } from "../../../constants";
import { DataUtils, MessageBrokerUtils, StringUtils } from "../../../utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { EmptyComponent } from "../EmptyComponent";
import Loadable from "react-loadable";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useNoScroll } from "../../../hooks";

const THEME = DataUtils.getDataStoreValue("ThemeData")?.Theme || "Default";

const InnerSpinner = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "spinner/[request]" */

      `./${StringUtils.capitalize(THEME)}/index`
    ),
  loading: EmptyComponent
});

InnerSpinner.preload();

export const VisibleSpinner = ({ fullOverlaySpinner }) => (
  <div
    id="spinner"
    className={classNames("inner-spinner", {
      fullOverlaySpinner: fullOverlaySpinner
    })}
    style={{ display: "flex" }}
  >
    <InnerSpinner />
  </div>
);

VisibleSpinner.propTypes = {
  fullOverlaySpinner: PropTypes.bool
};

const Timeout = {
  SLOW_CONNECTION_TIMEOUT: 20000,
  NORMAL_CONNECTION_TIMEOUT: 15000
};

const isSlowConnection = () => {
  const testConnectionFn =
    window.preloader && window.preloader.isSlowConnection;

  if (typeof testConnectionFn === "function") return testConnectionFn();

  return false;
};

const showError = () => {
  return (
    window.preloader &&
    typeof window.preloader.showErrorScreen === "function" &&
    window.preloader.showErrorScreen()
  );
};

const getTimeout = () => {
  const slowConnection = isSlowConnection();
  return slowConnection
    ? Timeout.SLOW_CONNECTION_TIMEOUT
    : Timeout.NORMAL_CONNECTION_TIMEOUT;
};

const getSpinner = (publisher, spinners) => {
  return spinners[publisher];
};

const Spinner = () => {
  const [spinners, setSpinners] = useState({});
  const [errorContainer, setErrorContainer] = useState(null);
  const [action, setAction] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  useEffect(() => {
    const spinnerVisibleSubscription = MessageBrokerUtils.subscribeSync({
      channel: Channel.VIEW_INJECTOR,
      topic: Topic.SetVisible,
      callback: (data, envelope) => {
        console.log("[SPINNER] - Got data:", data, envelope);
        setAction(envelope);
      }
    });
    return () => {
      spinnerVisibleSubscription?.unsubscribe();
    };
  }, []);

  const setErrorState = useCallback(
    publisher => {
      const spinner = getSpinner(publisher, spinners);
      const newSpinners = {
        ...spinners,
        [publisher]: {
          publisher,
          isVisible: false,
          isError: true,
          timer: spinner?.timer
        }
      };

      setSpinners(newSpinners);
      console.log(
        `[SPINNER]: setErrorState: error on publisher: ${publisher} `,
        newSpinners
      );
    },
    [spinners]
  );

  const addSpinner = useCallback(
    (spinners, publisher) => {
      const spinner = getSpinner(publisher, spinners);
      if (spinner?.timer) {
        clearTimeout(spinner.timer);
      }

      const timer = setTimeout(() => {
        console.log(
          `[SPINNER] - timeout hit for timer ${timer}, publisher: ${publisher}`
        );
        setErrorState(publisher);
      }, getTimeout());

      const newSpinnerStructure = {
        ...spinners,
        [publisher]: {
          publisher,
          isVisible: true,
          isError: false,
          timer: timer
        }
      };
      console.log(`[SPINNER]: addSpinner: `, newSpinnerStructure);

      setSpinners(newSpinnerStructure);
    },
    [setErrorState]
  );

  const setErrorOnSpinner = useCallback((spinners, publisher) => {
    const spinner = getSpinner(publisher, spinners);
    if (spinner) {
      if (spinner.timer) {
        clearTimeout(spinner.timer);
      }
      const newSpinners = {
        ...spinners,
        [publisher]: {
          publisher,
          isVisible: false,
          isError: true,
          timer: null
        }
      };
      setSpinners(newSpinners);
      console.log(`[SPINNER]: setErrorOnSpinner: `, newSpinners);
    }
  }, []);

  const removeSpinner = useCallback(
    (spinners, publisher, clearError = false) => {
      const spinner = getSpinner(publisher, spinners);
      if (spinner) {
        if (spinner.timer) {
          clearTimeout(spinner.timer);
          spinner.timer = null;
        }
        const newSpinners = {
          ...spinners
        };

        delete newSpinners[publisher];

        if (clearError) {
          Object.keys(newSpinners).forEach(publisher => {
            newSpinners[publisher].isError = false;
          });
        }

        setSpinners(newSpinners);
        console.log(`[SPINNER]: removeSpinner: `, newSpinners);
      }
    },
    []
  );

  useEffect(() => {
    if (action === lastAction) return;
    const {
      data: { visible, isError, clearError },
      headers: { publisher }
    } = action;

    if (visible) {
      if (!isError) {
        addSpinner(spinners, publisher);
      } else {
        setErrorOnSpinner(spinners, publisher);
      }
    } else {
      removeSpinner(spinners, publisher, clearError);
    }

    setLastAction(action);
  }, [
    action,
    addSpinner,
    lastAction,
    removeSpinner,
    setErrorOnSpinner,
    spinners
  ]);

  const hasErrors = useMemo(
    () => Object.values(spinners).find(elem => elem.isError),
    [spinners]
  );

  useEffect(() => {
    if (hasErrors) {
      console.log("[SPINNER] - setting error container...");
      setErrorContainer(showError());
    } else if (errorContainer) {
      console.log("[SPINNER] - removing error container...");
      errorContainer.remove();
    }
  }, [errorContainer, hasErrors, spinners]);

  const isVisible = useMemo(
    () => Object.values(spinners).some(spinner => spinner.isVisible),
    [spinners]
  );

  useEffect(() => {
    console.log("[SPINNER] state:", spinners);
  }, [spinners]);

  useNoScroll("inner-spinner", isVisible);
  return isVisible && <VisibleSpinner />;
};

export default Spinner;
